/**
 * Where the vault lives, and how to reach it.
 *
 * Stored in localStorage. The token is a fine-grained PAT scoped to one repo
 * with Contents: read and write — so the blast radius of a leak is that repo
 * and nothing else. That is a deliberate trade: a single-user static app has
 * nowhere better to put a credential, and the alternative (a proxy service
 * holding tokens server-side) is the right answer only once there are multiple
 * users. See README for the threat model.
 */

const STORAGE_KEY = 'hud:connection';

export interface GitHubConnection {
  kind: 'github';
  owner: string;
  repo: string;
  branch: string;
  token: string;
}

/** The Vite dev middleware over a local folder. No credentials involved. */
export interface LocalConnection {
  kind: 'local';
}

export type Connection = GitHubConnection | LocalConnection;

export function loadConnection(): Connection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Connection;
    if (parsed.kind === 'local') return parsed;
    if (parsed.kind === 'github' && parsed.owner && parsed.repo && parsed.token) return parsed;
    return null;
  } catch {
    return null;
  }
}

export function saveConnection(connection: Connection): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(connection));
}

export function clearConnection(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * What to use when nothing is configured.
 *
 * In dev that's the local folder, so `npm run dev` needs no setup. In a
 * deployed build there is no local folder, so the setup screen has to run.
 */
export function defaultConnection(): Connection | null {
  return import.meta.env.DEV ? { kind: 'local' } : null;
}
